import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bot,
  PenLine,
  Laptop,
  Users,
  Monitor,
  MessageSquare,
  Search,
  X,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useModulesStore } from '@/stores/modules.ts'
import { Button, Modal, useToast } from '@/components/ui'

interface NeuesAngebotModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onManualMode: () => void
  /** If set, ticketId is sent with POST /quotes and the mode selection step is skipped */
  ticketId?: string
  /** Pre-filled customer name from ticket data — triggers an automatic search when opened */
  initialCustomerName?: string
  /** TANSS-Unternehmens-ID aus dem Ticket — wird zur Auto-Auswahl des passenden Kunden genutzt */
  initialCustomerTanssId?: string
  /** Callback nach erfolgreicher Quote-Erstellung — wenn gesetzt, wird statt navigate() aufgerufen */
  onQuoteCreated?: (quoteId: string) => void
}

type Scenario = 'laptop' | 'teams' | 'meetingRoom' | 'free'

interface CustomerResult {
  id: string
  name: string
  source: 'sap' | 'tanss'
  sapBpCode?: string
  tenantId?: string
}

const SCENARIO_OPTIONS: Array<{
  value: Scenario
  label: string
  description: string
  icon: typeof Laptop
}> = [
  { value: 'laptop', label: 'Client-Hardware', description: 'Laptop, Garantie-Erweiterung, Inbetriebnahme', icon: Laptop },
  { value: 'teams', label: 'Teams-Migration', description: 'Lizenzen, Telefonie, Migration', icon: Users },
  { value: 'meetingRoom', label: 'Besprechungsraum', description: 'TV, Yealink-Bundle, Dienstleistung', icon: Monitor },
  { value: 'free', label: 'Freier Dialog', description: 'Kein vorgegebener Ablauf', icon: MessageSquare },
]

export function NeuesAngebotModal({
  open,
  onOpenChange,
  onManualMode,
  ticketId,
  initialCustomerName,
  initialCustomerTanssId,
  onQuoteCreated,
}: NeuesAngebotModalProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const isKiAllowed = useModulesStore((s) => s.isAllowed('sap-auftraege'))

  // When ticketId is provided we skip the mode selection and go directly to ki-setup
  const [step, setStep] = useState<'mode' | 'ki-setup'>(ticketId ? 'ki-setup' : 'mode')

  const [scenario, setScenario] = useState<Scenario>('free')
  const [customerQuery, setCustomerQuery] = useState(initialCustomerName ?? '')
  const [suggestions, setSuggestions] = useState<CustomerResult[]>([])
  const [selectedPartner, setSelectedPartner] = useState<CustomerResult | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const didAutoSearchRef = useRef(false)

  // Teams scenario requires a customer with a SAP BP code for get_customer_context
  const teamsWithoutCustomer = scenario === 'teams' && !selectedPartner

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep(ticketId ? 'ki-setup' : 'mode')
      setScenario('free')
      setCustomerQuery(initialCustomerName ?? '')
      setSuggestions([])
      setSelectedPartner(null)
      setShowSuggestions(false)
      didAutoSearchRef.current = false
    }
  }, [open, ticketId, initialCustomerName])

  // Auto-search and pre-select customer when opened in ticket context
  useEffect(() => {
    if (!open || !initialCustomerName || didAutoSearchRef.current) return
    if (step !== 'ki-setup') return
    didAutoSearchRef.current = true
    ;(async () => {
      setSearchLoading(true)
      try {
        const { data } = await apiClient.get('/quotes/customers/search', {
          params: { q: initialCustomerName },
        })
        const results: CustomerResult[] = Array.isArray(data) ? data : []
        const match =
          (initialCustomerTanssId
            ? results.find((r) => r.source === 'tanss' && r.id === initialCustomerTanssId)
            : null) ??
          results.find((r) => r.name === initialCustomerName) ??
          null
        if (match) {
          setSelectedPartner(match)
          setCustomerQuery(match.name)
        }
      } catch {
        // Silent: search field stays pre-filled with the name text
      } finally {
        setSearchLoading(false)
      }
    })()
  }, [open, step, initialCustomerName, initialCustomerTanssId])

  // Debounced partner search
  useEffect(() => {
    if (selectedPartner) return
    if (customerQuery.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const { data } = await apiClient.get('/quotes/customers/search', {
          params: { q: customerQuery },
        })
        const list: CustomerResult[] = Array.isArray(data) ? data : []
        setSuggestions(list)
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
        setShowSuggestions(false)
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [customerQuery, selectedPartner])

  function selectPartner(partner: CustomerResult) {
    setSelectedPartner(partner)
    setCustomerQuery(partner.name)
    setShowSuggestions(false)
    setSuggestions([])
  }

  function clearPartner() {
    setSelectedPartner(null)
    setCustomerQuery('')
    setSuggestions([])
    setShowSuggestions(false)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  async function handleStart() {
    setSubmitLoading(true)
    try {
      const body: Record<string, string> = { scenario }
      if (selectedPartner) {
        body.customerName = selectedPartner.name
        if (selectedPartner.sapBpCode) body.customerSapBpCode = selectedPartner.sapBpCode
        if (selectedPartner.source === 'tanss') body.customerTanssId = selectedPartner.id
        if (selectedPartner.tenantId) body.tenantId = selectedPartner.tenantId
      }
      if (ticketId) body.ticketId = ticketId
      const { data } = await apiClient.post('/quotes', body)
      const quoteId: string = data?.data?.id
      if (!quoteId) throw new Error('Keine Quote-ID in der Antwort')
      onOpenChange(false)
      if (onQuoteCreated) {
        onQuoteCreated(quoteId)
      } else {
        navigate(`/auftraege/angebot/${quoteId}`)
      }
    } catch (err) {
      toast({
        title: 'Angebot konnte nicht angelegt werden',
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        tone: 'danger',
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  function handleManualModus() {
    onOpenChange(false)
    onManualMode()
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Neues Angebot erstellen"
      size={step === 'ki-setup' ? 'lg' : 'md'}
    >
      {step === 'mode' && (
        <>
          <div className="space-y-3">
            <p className="text-body text-gray-600">Wie möchtest du das Angebot erstellen?</p>

            {isKiAllowed ? (
              <button
                type="button"
                onClick={() => setStep('ki-setup')}
                className="w-full flex items-start gap-4 rounded-lg border-2 border-brand-600 bg-brand-50 p-4 text-left hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
              >
                <Bot className="w-6 h-6 mt-0.5 text-brand-600 shrink-0" />
                <div>
                  <p className="font-semibold text-brand-700">KI-Modus — sofort loslegen</p>
                  <p className="text-caption text-gray-500 mt-0.5">
                    Dialogbasiert mit KI-Unterstützung: Artikelsuche, Standards, Kunden-Kontext
                  </p>
                </div>
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleManualModus}
              className="w-full flex items-start gap-4 rounded-lg border-2 border-gray-200 bg-white p-4 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 transition-colors"
            >
              <PenLine className="w-6 h-6 mt-0.5 text-gray-500 shrink-0" />
              <div>
                <p className="font-semibold text-gray-700">Manuell</p>
                <p className="text-caption text-gray-500 mt-0.5">
                  Angebot direkt in SAP Business One erfassen
                </p>
              </div>
            </button>
          </div>

          <div className="mt-6 flex justify-end">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
          </div>
        </>
      )}

      {step === 'ki-setup' && (
        <>
          <div className="space-y-6">
            {/* Customer search */}
            <div>
              <label className="block text-small font-semibold text-gray-700 mb-1.5">
                Kunde <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={customerQuery}
                    onChange={(e) => {
                      if (selectedPartner) clearPartner()
                      setCustomerQuery(e.target.value)
                    }}
                    placeholder="Kundenname oder Kundennummer suchen…"
                    className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                    autoComplete="off"
                  />
                  {(customerQuery || selectedPartner) && (
                    <button
                      type="button"
                      onClick={clearPartner}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Auswahl löschen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {showSuggestions && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-md max-h-48 overflow-auto">
                    {searchLoading ? (
                      <li className="px-3 py-2 text-small text-gray-500">Suche…</li>
                    ) : suggestions.length === 0 ? (
                      <li className="px-3 py-2 text-small text-gray-500">Keine Treffer</li>
                    ) : (
                      suggestions.map((p) => (
                        <li key={`${p.source}-${p.id}`}>
                          <button
                            type="button"
                            onClick={() => selectPartner(p)}
                            className="w-full text-left px-3 py-2 text-small hover:bg-brand-50 focus:outline-none focus:bg-brand-50"
                          >
                            <span className="font-medium text-gray-900">{p.name}</span>
                            {p.sapBpCode && (
                              <span className="ml-2 text-gray-400">{p.sapBpCode}</span>
                            )}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
              {selectedPartner && (
                <p className="mt-1 text-caption text-brand-700">
                  ✓ {selectedPartner.name}
                  {selectedPartner.sapBpCode && ` (${selectedPartner.sapBpCode})`}
                </p>
              )}
            </div>

            {/* Scenario cards */}
            <div>
              <p className="text-small font-semibold text-gray-700 mb-2">Szenario</p>
              <div className="grid grid-cols-2 gap-2">
                {SCENARIO_OPTIONS.map(({ value, label, description, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setScenario(value)}
                    className={[
                      'flex flex-col gap-1 rounded-lg border-2 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors',
                      scenario === value
                        ? 'border-brand-600 bg-brand-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50',
                    ].join(' ')}
                  >
                    <Icon
                      className={`w-5 h-5 ${scenario === value ? 'text-brand-600' : 'text-gray-400'}`}
                    />
                    <span
                      className={`text-small font-semibold ${scenario === value ? 'text-brand-700' : 'text-gray-700'}`}
                    >
                      {label}
                    </span>
                    <span className="text-caption text-gray-500">{description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Teams: warn if no customer selected */}
          {teamsWithoutCustomer && (
            <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-small text-amber-800">
              Für das Teams-Migrations-Szenario ist ein Kunde erforderlich — die KI ruft automatisch den Kunden-Kontext ab (Lizenzen, Telefonie). Bitte oben einen Kunden auswählen.
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            {/* In ticket context there's no mode step to go back to */}
            {!ticketId ? (
              <Button variant="ghost" onClick={() => setStep('mode')}>
                Zurück
              </Button>
            ) : (
              <div />
            )}
            <Button
              variant="primary"
              loading={submitLoading}
              onClick={handleStart}
              disabled={teamsWithoutCustomer}
              leadingIcon={<Bot className="w-4 h-4" />}
            >
              Starten
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
